import type { RenderContext, VideoFrame } from '../types';
import { WebGLShaderProgram } from '../gpu/shader';
import { BaseEffect } from './base';
import { glitchHash } from './glitch';
import { VERTEX_FULLSCREEN, FRAG_VHS } from '../shaders/glsl';

const DEFAULTS = {
  intensity: 0.6,
  rgbOffset: 3,
  noiseAmount: 0.2,
  scanLineOpacity: 0.35,
  blur: 0.4,
};

let vhsProg: WebGLShaderProgram | null = null;

/** VHS stylize — glitch + noise + blur + scanlines (Phase 8, Step 22). */
export class VhsEffect extends BaseEffect {
  seed: number;

  constructor(id: string, clip: Partial<import('../types').EffectTimelineClip>) {
    super(id, 'vhs', clip, DEFAULTS);
    this.seed = clip.seed ?? 17;
  }

  protected renderGpu(frame: VideoFrame, params: Record<string, number>, context: RenderContext): VideoFrame {
    const gl = context.gpu!.gl;
    if (!vhsProg) vhsProg = new WebGLShaderProgram(gl, VERTEX_FULLSCREEN, FRAG_VHS);
    const seed = this.seed + context.frameIndex;
    return this.runShaderPass(frame, vhsProg, (_gl, prog) => {
      prog.setUniform1i('u_texture', 0);
      prog.setUniform1f('u_intensity', params.intensity);
      prog.setUniform1f('u_rgbOffset', params.rgbOffset);
      prog.setUniform1f('u_noiseAmount', params.noiseAmount);
      prog.setUniform1f('u_scanLineOpacity', params.scanLineOpacity);
      prog.setUniform1f('u_blur', params.blur);
      prog.setUniform1f('u_seed', seed);
      prog.setUniform1f('u_time', context.currentTime);
    }, context);
  }

  protected renderCpu(frame: VideoFrame, params: Record<string, number>, context: RenderContext): VideoFrame {
    const canvas = document.createElement('canvas');
    canvas.width = frame.width;
    canvas.height = frame.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return frame;
    const w = frame.width;
    const h = frame.height;
    const srcCtx = (frame.canvas as HTMLCanvasElement).getContext('2d');
    if (!srcCtx) {
      ctx.drawImage(frame.canvas as CanvasImageSource, 0, 0);
      return { width: w, height: h, canvas, texture: null };
    }
    const src = srcCtx.getImageData(0, 0, w, h);
    const out = ctx.createImageData(w, h);
    const seed = this.seed + context.frameIndex;
    const shift = Math.round(params.rgbOffset * params.intensity * 0.6);
    for (let y = 0; y < h; y += 1) {
      const wobble = Math.round(Math.sin(y * 0.08 + context.currentTime * 3) * params.intensity * w * 0.003);
      for (let x = 0; x < w; x += 1) {
        const sx = Math.min(w - 1, Math.max(0, x + wobble));
        const ri = (y * w + Math.min(w - 1, sx + shift)) * 4;
        const gi = (y * w + sx) * 4;
        const bi = (y * w + Math.max(0, sx - shift)) * 4;
        const oi = (y * w + x) * 4;
        out.data[oi] = src.data[ri];
        out.data[oi + 1] = src.data[gi + 1];
        out.data[oi + 2] = src.data[bi + 2];
        out.data[oi + 3] = 255;
        if (y % 2 === 0) {
          const dim = 1 - params.scanLineOpacity * params.intensity;
          out.data[oi] *= dim;
          out.data[oi + 1] *= dim;
          out.data[oi + 2] *= dim;
        }
        const n = glitchHash(x, y, seed) * params.noiseAmount * params.intensity * 255;
        out.data[oi] = Math.min(255, out.data[oi] + n - params.noiseAmount * params.intensity * 128);
        out.data[oi + 1] = Math.min(255, out.data[oi + 1] + n - params.noiseAmount * params.intensity * 128);
        out.data[oi + 2] = Math.min(255, out.data[oi + 2] + n - params.noiseAmount * params.intensity * 128);
      }
    }
    ctx.putImageData(out, 0, 0);
    return { width: w, height: h, canvas, texture: null };
  }
}
